import { observer } from 'mobx-react-lite';
import * as React from 'react';
import isSaveViewDialogShown from '../selectors/isSaveViewDialogShown';
import getCurrentlyEditedView from '../selectors/getCurrentlyEditedView';
import getViewName from '../selectors/getViewName';
import getCharmId from '../selectors/getCharmId';
import { hideAddOrEditSaveViewDialog } from '../actions/publicActions';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { TextField } from '@fluentui/react/lib/TextField';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import OwaDialog from 'owa-dialog/lib/components/OwaDialog';
import { DialogFooter } from '@fluentui/react/lib/Dialog';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import loc from 'owa-localize';
import {
    lazyAddSavedCalendarView,
    lazyEditSavedCalendarView,
    DraftSaveCalendarViewConfig,
    SaveCalendarViewConfig,
} from 'owa-save-calendar-view-data';
import { getSelectedCalendars } from 'owa-calendar-surface-calendars-store';
import {
    saveViewDialogAddTitle,
    saveViewDialogEditTitle,
    viewInputHeader,
    saveViewButton,
    cancelViewButton,
} from './SaveViewDialog.locstring.json';
import { logUsage } from 'owa-analytics';
import { CharmControlColorPattern, CharmPickerBackground } from 'owa-charmcontrol';
import CharmPicker from 'owa-charmpicker/lib/components/CharmPicker';
import { getNoCharmInfoObject } from 'owa-calendar-charms-store';
import { updateDialogCharm } from '../mutators/updateDialogCharmMutator';
import { updateViewName } from '../mutators/updateViewNameMutator';
import { getThemeColorScheme } from 'owa-color-utils/lib/colorUtils';
import { getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import { getSurfaceStore } from 'owa-calendar-surface-surface-store';

import styles from './SaveViewDialog.scss';

export default observer(function SaveViewDialog() {
    let currentlyEditedView: SaveCalendarViewConfig = getCurrentlyEditedView();
    const isCurrentlyEditingView: boolean = currentlyEditedView != null;
    let currentViewName: string = getViewName();
    let currentIconId: number = getCharmId();
    if (currentIconId == null) {
        currentIconId = 0;
    }
    let charm = getNoCharmInfoObject();
    charm.IconId = currentIconId;
    const colorScheme = getThemeColorScheme();
    const onSaveClick = () => {
        if (isCurrentlyEditingView) {
            let updatedView: SaveCalendarViewConfig = {
                id: currentlyEditedView.id,
                selectedCalendarIds: currentlyEditedView.selectedCalendarIds.slice(),
                dateRangeType: currentlyEditedView.dateRangeType,
                splitView: currentlyEditedView.splitView,
                viewName: currentViewName,
                chosenHotkey: null, // TODO: VSO 90135: add hotkey support
                numDaysInDayRange: currentlyEditedView.numDaysInDayRange,
                viewIcon: currentIconId.toString(),
            };
            lazyEditSavedCalendarView.importAndExecute(updatedView);
        } else {
            logUsage('createSaveView');
            const currentDateRangeType: DateRangeType = getSurfaceStore().dateRangeType;
            const selectedCalendarIds: string[] = getSelectedCalendars().map(calendar => {
                return calendar.calendarId.id;
            });

            // Split view should not be enabled if in month view or 1 calendar is selected
            const splitViewStatus: boolean =
                currentDateRangeType == DateRangeType.Month || selectedCalendarIds.length < 2
                    ? false
                    : getSurfaceStore().isInSplitView;
            // TODO VSO 89940: Add logic for adding a saved view that already exists
            let savedViewConfig: DraftSaveCalendarViewConfig = {
                selectedCalendarIds: selectedCalendarIds,
                dateRangeType: currentDateRangeType,
                splitView: splitViewStatus,
                viewName: currentViewName,
                chosenHotkey: null, // TODO: VSO 90135: add hotkey support
                numDaysInDayRange: getNumDaysInDayRange(),
                viewIcon: currentIconId.toString(),
            };
            lazyAddSavedCalendarView.importAndExecute(savedViewConfig);
        }
        hideAddOrEditSaveViewDialog();
    };
    const onViewInputChange = (event: React.FormEvent<HTMLInputElement>, input: string) => {
        currentViewName = input;
        updateViewName(currentViewName);
    };
    const onCharmChange = charm => {
        updateDialogCharm(charm.IconId);
    };
    return (
        <OwaDialog
            hidden={!isSaveViewDialogShown()}
            onDismiss={hideAddOrEditSaveViewDialog}
            modalProps={{
                containerClassName: styles.saveViewDialog,
            }}
            dialogContentProps={{
                title: isCurrentlyEditingView
                    ? loc(saveViewDialogEditTitle)
                    : loc(saveViewDialogAddTitle),
                titleProps: {
                    className: styles.title,
                },
                showCloseButton: true,
            }}>
            <Label className={styles.viewNameLabel}> {loc(viewInputHeader)} </Label>
            <Stack horizontal={true} className={styles.charmAndViewNameStack}>
                <div className={styles.charmPickerCell}>
                    <CharmPicker
                        charmClassName={styles.charmIconTitle}
                        buttonClassName={styles.charmButton}
                        selectedCharm={charm}
                        onSelectIcon={onCharmChange}
                        colorScheme={colorScheme}
                        colorPattern={CharmControlColorPattern.Normal}
                        background={CharmPickerBackground.Light}
                    />
                </div>
                <TextField
                    className={styles.viewNameInput}
                    onChange={onViewInputChange}
                    defaultValue={isCurrentlyEditingView ? currentlyEditedView.viewName : ''}
                    styles={{
                        fieldGroup: styles.viewNameInputTextfield,
                    }}
                    ariaLabel={loc(viewInputHeader)}></TextField>
            </Stack>
            <DialogFooter className={styles.dialogFooter}>
                <PrimaryButton text={loc(saveViewButton)} onClick={onSaveClick} />
                <DefaultButton text={loc(cancelViewButton)} onClick={hideAddOrEditSaveViewDialog} />
            </DialogFooter>
        </OwaDialog>
    );
});
